import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Robot, Globe, Lightning, ChartBar, Code, CurrencyDollar, Key, Palette } from "@phosphor-icons/react";
import Marquee from "react-fast-marquee";
import axios from "axios";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

const LOGO_URLS = [
  "https://images.unsplash.com/photo-1765448808249-a3610f38e612?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxjb21wYW55JTIwbG9nbyUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzc2MzY4OTQzfDA&ixlib=rb-4.1.0&q=85&w=80",
  "https://images.unsplash.com/photo-1605514449453-c6ed5f1963e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxjb21wYW55JTIwbG9nbyUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzc2MzY4OTQzfDA&ixlib=rb-4.1.0&q=85&w=80",
];

const TESTIMONIALS = [
  {
    name: "Sarah Chen",
    role: "Head of Support, TechFlow",
    text: "Cut our response time by 73%. The RAG pipeline is genuinely accurate.",
    img: "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjM2ODk0M3ww&ixlib=rb-4.1.0&q=85&w=80",
  },
  {
    name: "Marcus Rivera",
    role: "Founder, LaunchPad",
    text: "Deployed on 3 client sites in a single afternoon. No code required.",
    img: "https://images.unsplash.com/photo-1769636929388-99eff95d3bf1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjM2ODk0M3ww&ixlib=rb-4.1.0&q=85&w=80",
  },
  {
    name: "Aisha Patel",
    role: "CTO, Grova",
    text: "The embed widget is clean. Our customers think it's a custom build.",
    img: "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjM2ODk0M3ww&ixlib=rb-4.1.0&q=85&w=80",
  },
];

const STEPS = [
  { icon: Globe, label: "01 / SCRAPE", title: "Paste your URL", desc: "We crawl your entire website and extract meaningful content automatically." },
  { icon: Lightning, label: "02 / INDEX", title: "AI chunks & indexes", desc: "Content is split into ~500-token chunks and stored for instant retrieval." },
  { icon: Robot, label: "03 / CHAT", title: "Deploy your chatbot", desc: "Embed a single script tag. Your visitors get instant, accurate answers." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({ symbol: "$", amount: 49, currency: "USD", display: "$49/month" });
  const [loadingPricing, setLoadingPricing] = useState(true);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/billing/pricing`);
      setPricing(res.data);
    } catch (err) {
      console.error("Pricing fetch failed:", err);
    } finally {
      setLoadingPricing(false);
    }
  };

  const proPrice = pricing.display || `$49/month`;
  const proSymbol = pricing.symbol || "$";
  const proAmount = pricing.amount || 49;

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      {/* Nav */}
      <nav className="border-b border-[#E5E7EB] sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-heading text-xl font-black tracking-tight" data-testid="landing-logo">
            ChatBot<span className="text-[#002FA7]">Builder</span>
          </span>
          <div className="flex items-center gap-4">
            <a href="#pricing" className="text-sm font-medium text-[#6B7280] hover:text-[#0A0A0A] transition-colors hidden sm:block" data-testid="nav-pricing-link">
              Pricing
            </a>
            <Button
              onClick={() => navigate("/auth")}
              variant="outline"
              className="rounded-none border-[#D1D5DB] hover:bg-[#F3F4F6] text-sm h-9"
              data-testid="nav-login-button"
            >
              Sign in
            </Button>
            <Button
              onClick={() => navigate("/auth")}
              className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white text-sm h-9 transition-transform active:scale-[0.98]"
              data-testid="nav-get-started-button"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="grid-bg border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6" data-testid="hero-label">
              No-Code AI Chatbot Platform
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-[#0A0A0A]" data-testid="hero-heading">
              Turn any website into a
              <br />
              <span className="text-[#002FA7]">smart chatbot</span>
            </h1>
            <p className="text-base md:text-lg text-[#0A0A0A]/70 mt-6 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              Scrape your site, build a RAG pipeline, and deploy an AI chatbot your customers will love. Zero code. Five minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Button
                onClick={() => navigate("/auth")}
                className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white h-12 px-8 text-sm font-semibold transition-transform active:scale-[0.98]"
                data-testid="hero-cta-button"
              >
                Start Building Free
                <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center h-12 px-8 border border-[#D1D5DB] text-sm font-semibold text-[#0A0A0A] hover:bg-[#F3F4F6] transition-colors"
                data-testid="hero-secondary-button"
              >
                See how it works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Marquee */}
      <section className="border-b border-[#E5E7EB] py-8 bg-[#F9FAFB]">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 text-center mb-6">
          Trusted by growing teams
        </div>
        <Marquee speed={30} gradient={false} pauseOnHover>
          {[...LOGO_URLS, ...LOGO_URLS, ...LOGO_URLS].map((url, i) => (
            <img
              key={i}
              src={url}
              alt="Partner logo"
              className="h-8 mx-16 opacity-40 grayscale object-contain"
              loading="lazy"
            />
          ))}
        </Marquee>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">How it works</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              Three steps. One script tag.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border border-[#E5E7EB]">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className={`p-8 md:p-10 ${i < 2 ? "border-b md:border-b-0 md:border-r border-[#E5E7EB]" : ""} group hover:bg-[#F9FAFB] transition-colors`}
                data-testid={`how-it-works-step-${i}`}
              >
                <div className="w-12 h-12 border border-[#D1D5DB] flex items-center justify-center mb-6 group-hover:border-[#002FA7] group-hover:text-[#002FA7] transition-colors">
                  <step.icon size={24} weight="bold" />
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-3">{step.label}</div>
                <h3 className="font-heading text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-sm text-[#0A0A0A]/60 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Features</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              Everything you need to ship
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Robot, title: "Claude-Powered AI", desc: "RAG pipeline with Groq LLaMA 3.3 for accurate, context-aware responses." },
              { icon: Globe, title: "Website Scraping", desc: "Automatic content extraction from any public URL. No manual uploads." },
              { icon: Code, title: "One-Line Embed", desc: "Drop a single script tag on your site. Your chatbot is live instantly." },
              { icon: ChartBar, title: "Usage Analytics", desc: "Track messages, monitor usage, and understand your visitors." },
              { icon: Lightning, title: "Fast Retrieval", desc: "TF-IDF powered search with easy upgrade path to vector databases." },
              { icon: Key, title: "API Access", desc: "Programmatic access with API keys. Integrate with your existing stack." },
              { icon: Palette, title: "White-Label", desc: "Custom branding, domain, and colors. Your clients never see our name." },
              { icon: CurrencyDollar, title: "Simple Pricing", desc: `Free tier to start. Pro at ${proPrice} for unlimited power.` },
            ].map((feat, i) => (
              <div
                key={i}
                className="border border-[#E5E7EB] bg-white p-8 hover:-translate-y-1 hover:shadow-lg hover:border-[#D1D5DB] transition-all duration-200"
                data-testid={`feature-card-${i}`}
              >
                <feat.icon size={28} weight="bold" className="text-[#002FA7] mb-4" />
                <h3 className="font-heading text-lg font-bold mb-2">{feat.title}</h3>
                <p className="text-sm text-[#0A0A0A]/60 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="mb-16 text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Pricing</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              Start free. Scale when ready.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 max-w-2xl mx-auto border border-[#E5E7EB]">
            {/* Free */}
            <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-[#E5E7EB]" data-testid="pricing-free">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-4">Free</div>
              <div className="font-heading text-5xl font-black tracking-tighter">$0</div>
              <div className="text-sm text-[#6B7280] mb-8">/month</div>
              <ul className="space-y-3 text-sm text-[#0A0A0A]/80 mb-8">
                <li className="flex items-start gap-2"><span className="text-[#002FA7] font-bold mt-0.5">--</span> 100 messages/month</li>
                <li className="flex items-start gap-2"><span className="text-[#002FA7] font-bold mt-0.5">--</span> 1 website integration</li>
                <li className="flex items-start gap-2"><span className="text-[#002FA7] font-bold mt-0.5">--</span> Standard chatbot widget</li>
              </ul>
              <Button
                onClick={() => navigate("/auth")}
                variant="outline"
                className="w-full rounded-none border-[#D1D5DB] h-11"
                data-testid="pricing-free-cta"
              >
                Get Started
              </Button>
            </div>
            {/* Pro */}
            <div className="p-8 md:p-10 bg-[#0A0A0A] text-white" data-testid="pricing-pro">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">Pro</div>
              <div className="font-heading text-5xl font-black tracking-tighter">
                {loadingPricing ? "..." : `${proSymbol}${proAmount}`}
              </div>
              <div className="text-sm text-white/50 mb-8">/month</div>
              <ul className="space-y-3 text-sm text-white/80 mb-8">
                <li className="flex items-start gap-2"><span className="text-[#002FA7] font-bold mt-0.5">--</span> Unlimited messages</li>
                <li className="flex items-start gap-2"><span className="text-[#FDE047] font-bold mt-0.5">--</span> Up to 10 websites</li>
                <li className="flex items-start gap-2"><span className="text-[#FDE047] font-bold mt-0.5">--</span> Custom branding</li>
                <li className="flex items-start gap-2"><span className="text-[#FDE047] font-bold mt-0.5">--</span> API Access</li>
                <li className="flex items-start gap-2"><span className="text-[#FDE047] font-bold mt-0.5">--</span> White-Label</li>
                <li className="flex items-start gap-2"><span className="text-[#FDE047] font-bold mt-0.5">--</span> Priority support</li>
              </ul>
              <Button
                onClick={() => navigate("/auth")}
                className="w-full rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white h-11 transition-transform active:scale-[0.98]"
                data-testid="pricing-pro-cta"
              >
                Upgrade to Pro
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Testimonials</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              What builders say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border border-[#E5E7EB]">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className={`bg-white p-8 ${i < 2 ? "border-b md:border-b-0 md:border-r border-[#E5E7EB]" : ""}`}
                data-testid={`testimonial-${i}`}
              >
                <p className="text-sm text-[#0A0A0A]/70 leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover border border-[#E5E7EB]" />
                  <div>
                    <div className="text-sm font-semibold text-[#0A0A0A]">{t.name}</div>
                    <div className="text-xs text-[#6B7280]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            Ready to build your chatbot?
          </h2>
          <p className="text-base text-white/50 mb-10 max-w-md mx-auto">
            Start free. No credit card required. Deploy in minutes.
          </p>
          <Button
            onClick={() => navigate("/auth")}
            className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white h-12 px-10 text-sm font-semibold transition-transform active:scale-[0.98]"
            data-testid="footer-cta-button"
          >
            Get Started Free
            <ArrowRight size={18} weight="bold" className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <span className="text-xs text-[#6B7280]">ChatBot Builder</span>
          <span className="text-xs text-[#6B7280]">Built with Groq AI</span>
        </div>
      </footer>
    </div>
  );
}
